import { Alert } from '@op/opds.opux';
import { type PurchasePathState } from '../../types/initialState';
import styles from '../CoverageSection/CoverageSection.module.scss';
import CoverageHeading from '../CoverageSelector/CoverageHeading';
import CoverageInfoModal from '../CoverageSelector/CoverageInfoModal';
import CoverageStepper from '../CoverageSelector/CoverageStepper';
import { DiscountType } from 'dto/CoveragePremium';
import { CoverageProduct } from 'dto/marketableProduct';
import i18n from 'i18n';
import DiscountAction, {
  type CustomerType,
} from 'individual/components/common/DiscountAction';
import {
  useSelectedPremiums,
  useSelectablePremiums,
  useLifeInsuranceFallbackAssigned,
} from 'state/selectors/coverageSelectors';
import { useAppStore } from 'state/store';
import { formatNumber, getInitialState, getSuggestedPremium } from 'utils';
import { getDiscount } from 'utils/premium-utils';

const LifeInsurance = () => {
  const selectedSumInsuredPremiums = useSelectedPremiums();
  const lifeSelectablePremiums = useSelectablePremiums(CoverageProduct.LIFE);
  const coveragePremiums = useAppStore.use.coveragePremiums();
  const lifeInsuranceFallbackPremiumAssigned =
    useLifeInsuranceFallbackAssigned();

  const { marketableProduct, partyInformation, authenticatedCustomer } =
    getInitialState() as PurchasePathState;

  const coverageProduct = CoverageProduct.LIFE;

  // Determine customer type for discount modal
  const getCustomerType = (): CustomerType => {
    if (partyInformation?.simulatedPrimeCustomer) {
      return 'simulatedPrime';
    }
    if (partyInformation?.ownerCustomer) {
      return 'owner';
    }
    if (partyInformation?.primeCustomer) {
      return 'prime';
    }
    return 'unAuthenticated';
  };

  const customerType = getCustomerType();

  if (!lifeSelectablePremiums) {
    // Return null to avoid error throwing on page refresh
    return null;
  }

  const coverage = marketableProduct.coverages.find(
    (c) => c.coverageProduct === coverageProduct
  );

  if (!coverage) {
    throw new Error(
      `No coverage found for coverageProduct: ${coverageProduct}`
    );
  }

  const lifePremium = selectedSumInsuredPremiums[coverageProduct];

  if (!lifePremium) {
    return null;
  }

  const discount = getDiscount(
    lifePremium,
    lifeSelectablePremiums,
    DiscountType.DISCRETIONARY_DISCOUNT
  );

  const suggestedPremium = coveragePremiums
    ? getSuggestedPremium(coveragePremiums, coverageProduct)
    : undefined;

  // Display alert of suggested sum being lower than min sum insured
  // When life selectable premiums does not contain the suggested sum insured
  const displayMinSumInsuredAlert =
    lifeInsuranceFallbackPremiumAssigned ||
    (suggestedPremium
      ? suggestedPremium.sumInsured < coverage.minSumInsured &&
        !lifeSelectablePremiums.find((p) => p.suggestedSumInsured.isSuggested)
      : false);

  return (
    <div
      data-testid={`coverageTile-${coverage.coverageProduct}`}
      className={styles.coverageTile}>
      <CoverageHeading
        coverage={coverage}
        premium={lifePremium}
        discountComponent={
          discount ? (
            <DiscountAction
              discount={discount}
              discountType={DiscountType.DISCRETIONARY_DISCOUNT}
              customerType={customerType}
              textKey={
                authenticatedCustomer
                  ? 'insurances.benefitIncluded'
                  : 'insurances.potentialBenefitIncluded'
              }
            />
          ) : null
        }
        displayDetails={true}
      />
      <CoverageStepper
        coverage={coverage}
        premium={lifePremium}
        selectablePremiums={lifeSelectablePremiums}
      />
      {displayMinSumInsuredAlert && (
        <Alert
          inline
          data-testid={`${coverageProduct}-minSumInsuredAlert`}
          id={`${coverageProduct}-minSumInsuredAlert`}>
          <p>
            {i18n.t(`coverageProduct.${coverageProduct}.minSumInsuredAlert`, {
              minSumInsured: formatNumber(coverage.minSumInsured),
              minSumInsuredWithAdditionalCoverages:
                coverage.minSumInsuredWhenOptionalCoverageSelected
                  ? formatNumber(
                      coverage.minSumInsuredWhenOptionalCoverageSelected
                    )
                  : '',
            })}
          </p>
        </Alert>
      )}
      <CoverageInfoModal coverage={coverage} />
    </div>
  );
};

export default LifeInsurance;
